import { Socket } from 'socket.io-client';

interface FingerprintDeviceResponse {
  template?: string;
  fingerprintId?: number;
  fingerprint_id?: number;
  matched?: boolean;
  match?: boolean;
  success?: boolean;
  error?: string;
  msg?: string;
}

class FingerprintSocketService {
  private socket: Socket | null = null;
  private pendingOperations: Map<string, {
    resolve: (value: FingerprintDeviceResponse) => void;
    reject: (error: Error) => void;
    timeout: NodeJS.Timeout;
  }> = new Map();

  /**
   * Initialize socket connection
   */
  initialize(socket: Socket) {
    this.socket = socket;
    this.setupEventListeners();
  }

  /**
   * Setup event listeners for fingerprint device responses
   */
  private setupEventListeners() {
    if (!this.socket) return;

    // Listen for fingerprint device events
    // Backend emits events like fp:DEVICE_ID:enroll, fp:DEVICE_ID:verify, etc.
    // We need to listen for all device events and route to the correct pending operation
    const eventTypes = ['enroll', 'verify', 'delete'];
    
    eventTypes.forEach(eventType => {
      // Use a wildcard listener - Socket.IO doesn't support wildcards directly,
      // so we'll need to listen to all possible device IDs or use a different approach
      // For now, we'll set up individual listeners when operations are initiated
    });

    // Listen for device status updates
    this.socket.on('fingerprint-device-status', (data: { deviceId: number; status: string }) => {
      // Emit custom event for device status changes
      window.dispatchEvent(new CustomEvent('fingerprint-device-status', { detail: data }));
    });
  }

  /**
   * Setup listener for device status events (fp:DEVICE_ID:status)
   */
  setupStatusListener(deviceId: number, callback: (status: string) => void) {
    if (!this.socket) return () => {};

    const eventName = `fp:${deviceId}:status`;
    
    const handler = (data: { msg?: string; deviceId?: number } | string) => {
      let statusMsg = '';
      if (typeof data === 'string') {
        statusMsg = data;
      } else if (data && typeof data === 'object') {
        statusMsg = data.msg || '';
      }
      
      if (statusMsg) {
        console.log(`[FingerprintSocketService] Status from device ${deviceId}:`, statusMsg);
        callback(statusMsg);
      }
    };

    this.socket.on(eventName, handler);

    // Return cleanup function
    return () => {
      if (this.socket) {
        this.socket.off(eventName, handler);
      }
    };
  }

  /**
   * Setup listener for specific device event
   */
  private setupDeviceEventListener(deviceId: number, eventType: string) {
    if (!this.socket) return;

    const eventName = `fp:${deviceId}:${eventType}`;
    
    // Remove existing listener if any
    this.socket.off(eventName);
    
    // Setup new listener
    this.socket.on(eventName, (data: FingerprintDeviceResponse) => {
      this.handleDeviceResponse(eventType, deviceId, data);
    });
  }

  /**
   * Handle device response
   */
  private handleDeviceResponse(eventType: string, deviceId: number, data: FingerprintDeviceResponse) {
    // Find matching pending operation by deviceId and eventType
    const operationKey = `${deviceId}:${eventType}`;
    const pendingOp = this.pendingOperations.get(operationKey);

    if (pendingOp) {
      clearTimeout(pendingOp.timeout);
      this.pendingOperations.delete(operationKey);
      pendingOp.resolve(data);
    }
  }

  /**
   * Send enrollment command to device
   */
  async enrollFingerprint(
    deviceId: number,
    employeeId: number,
    onStatusUpdate?: (status: string) => void
  ): Promise<FingerprintDeviceResponse> {
    if (!this.socket || !this.socket.connected) {
      throw new Error('Socket not connected');
    }

    return new Promise((resolve, reject) => {
      const operationKey = `${deviceId}:enroll`;
      
      // Setup listener for this specific device and event type
      this.setupDeviceEventListener(deviceId, 'enroll');
      
      const timeout = setTimeout(() => {
        this.pendingOperations.delete(operationKey);
        reject(new Error('Enrollment timeout: Device did not respond in time'));
      }, 30000); // 30 second timeout

      this.pendingOperations.set(operationKey, {
        resolve,
        reject,
        timeout
      });

      // Send enrollment event to backend (backend will forward to device)
      const eventName = `fp:${deviceId}:enroll`;
      this.socket!.emit(eventName, { employeeId });

      if (onStatusUpdate) {
        onStatusUpdate('Sending enrollment command to device...');
      }
    });
  }

  /**
   * Send verification command to device
   */
  async verifyFingerprint(
    deviceId: number,
    template: string,
    onStatusUpdate?: (status: string) => void
  ): Promise<FingerprintDeviceResponse> {
    if (!this.socket || !this.socket.connected) {
      throw new Error('Socket not connected');
    }

    return new Promise((resolve, reject) => {
      const operationKey = `${deviceId}:verify`;
      
      // Setup listener for this specific device and event type
      this.setupDeviceEventListener(deviceId, 'verify');
      
      const timeout = setTimeout(() => {
        this.pendingOperations.delete(operationKey);
        reject(new Error('Verification timeout: Device did not respond in time'));
      }, 15000); // 15 second timeout

      this.pendingOperations.set(operationKey, {
        resolve,
        reject,
        timeout
      });

      // Send verification event to backend (backend will forward to device)
      const eventName = `fp:${deviceId}:verify`;
      this.socket!.emit(eventName, { template });

      if (onStatusUpdate) {
        onStatusUpdate('Sending verification command to device...');
      }
    });
  }

  /**
   * Send delete command to device
   */
  async deleteFingerprint(
    deviceId: number,
    fingerprintId: number,
    onStatusUpdate?: (status: string) => void
  ): Promise<FingerprintDeviceResponse> {
    if (!this.socket || !this.socket.connected) {
      throw new Error('Socket not connected');
    }

    return new Promise((resolve, reject) => {
      const operationKey = `${deviceId}:delete`;
      
      // Setup listener for this specific device and event type
      this.setupDeviceEventListener(deviceId, 'delete');
      
      const timeout = setTimeout(() => {
        this.pendingOperations.delete(operationKey);
        reject(new Error('Delete timeout: Device did not respond in time'));
      }, 10000); // 10 second timeout

      this.pendingOperations.set(operationKey, {
        resolve,
        reject,
        timeout
      });

      // Send delete event to backend (backend will forward to device)
      const eventName = `fp:${deviceId}:delete`;
      this.socket!.emit(eventName, { fingerprintId });

      if (onStatusUpdate) {
        onStatusUpdate('Sending delete command to device...');
      }
    });
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket !== null && this.socket.connected;
  }

  /**
   * Cleanup
   */
  cleanup() {
    // Clear all pending operations
    this.pendingOperations.forEach(op => {
      clearTimeout(op.timeout);
      op.reject(new Error('Service cleanup'));
    });
    this.pendingOperations.clear();

    if (this.socket) {
      // Remove event listeners
      const eventTypes = ['enroll', 'verify', 'delete'];
      eventTypes.forEach(eventType => {
        this.socket!.off(`fp:*:${eventType}`);
      });
      this.socket.off('fingerprint-device-status');
    }
  }
}

const fingerprintSocketServiceInstance = new FingerprintSocketService();
export default fingerprintSocketServiceInstance;

